import { FormSchema, HandoverFormSchema } from "@/lib/form/schema"
import { FormData, FormParamStatus } from "@/lib/form/types"

export interface OfflineIpqcEmployee {
  employeeCode: string
  employeeName: string
}

export interface OfflineIpqcBundle {
  version: string
  checksum?: string
  generatedAt?: string
  context?: {
    plantId?: string
    processId?: string
    shiftId?: string
    shiftName?: string
    shiftSessionId?: string
    shiftSessionStartedAt?: string
    shiftStartDateTime?: string
    shiftEndDateTime?: string
    shiftStartTime?: string
    shiftEndTime?: string
  }
  cachedAt: string
  employees: OfflineIpqcEmployee[]
  products: FormSchema["products"]
}

export interface OfflineIpqcSessionMeta {
  clientSessionId: string
  serverSessionId?: string
  shiftId: string
  shiftSessionId: string
  processId: string
  startedAt: string
  employeeCode?: string
  plantId?: string
  isDirty: boolean
  hasDeferredSync?: boolean
  syncedAt?: string
  pendingFinalizeAt?: string
  handoverEmployeeCode?: string
}

export interface OfflineIpqcSession {
  meta: OfflineIpqcSessionMeta
  schema: FormSchema
  form: FormData
  handoverSchemas: HandoverFormSchema[]
  handoverForms: FormData[]
  lineStatus: Record<string, FormParamStatus | undefined>
  currProductIdx: number
  currLineIdx: number
  currHandoverIdx: number
  currHandoverSessionId: string
}

const DB_NAME = "darpan-offline"
const DB_VERSION = 1
const STORE_NAME = "kv"
const BUNDLE_KEY = "ipqc-offline-bundle"
const SESSION_KEY = "ipqc-offline-session"

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }

    request.onsuccess = () => {
      resolve(request.result)
    }

    request.onerror = () => {
      reject(request.error)
    }
  })
}

function withStore<T>(
  mode: IDBTransactionMode,
  handler: (store: IDBObjectStore) => IDBRequest<T> | void
): Promise<T | undefined> {
  return openDb().then(
    (db) =>
      new Promise<T | undefined>((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, mode)
        const store = transaction.objectStore(STORE_NAME)
        const request = handler(store)

        transaction.oncomplete = () => {
          db.close()
          if (!request) {
            resolve(undefined)
          }
        }

        transaction.onerror = () => {
          db.close()
          reject(transaction.error)
        }

        transaction.onabort = () => {
          db.close()
          reject(transaction.error)
        }

        if (request) {
          request.onsuccess = () => {
            resolve(request.result)
          }

          request.onerror = () => {
            reject(request.error)
          }
        }
      })
  )
}

async function putValue<T>(key: string, value: T): Promise<void> {
  await withStore("readwrite", (store) => {
    store.put(value, key)
  })
}

async function getValue<T>(key: string): Promise<T | null> {
  const result = await withStore<T>("readonly", (store) => store.get(key))
  return result ?? null
}

async function deleteValue(key: string): Promise<void> {
  await withStore("readwrite", (store) => {
    store.delete(key)
  })
}

export async function loadOfflineIpqcBundle(): Promise<OfflineIpqcBundle | null> {
  return getValue<OfflineIpqcBundle>(BUNDLE_KEY)
}

export async function saveOfflineIpqcBundle(
  bundle: OfflineIpqcBundle | null
): Promise<void> {
  if (!bundle) {
    await deleteValue(BUNDLE_KEY)
    return
  }

  await putValue(BUNDLE_KEY, bundle)
}

export async function loadOfflineIpqcSession(): Promise<OfflineIpqcSession | null> {
  return getValue<OfflineIpqcSession>(SESSION_KEY)
}

export async function saveOfflineIpqcSession(
  session: OfflineIpqcSession | null
): Promise<void> {
  if (!session) {
    await deleteValue(SESSION_KEY)
    return
  }

  await putValue(SESSION_KEY, session)
}

export async function clearOfflineIpqcStorage(): Promise<void> {
  await Promise.all([deleteValue(BUNDLE_KEY), deleteValue(SESSION_KEY)])
}

function normalizeOwnerValue(value?: string): string {
  return (value || "").trim().toLowerCase()
}

export function doesOfflineIpqcSessionMatchOwner(
  session: OfflineIpqcSession,
  owner: {
    employeeCode?: string
    plantId?: string
  }
): boolean {
  const sessionEmployeeCode = normalizeOwnerValue(session.meta.employeeCode)
  const sessionPlantId = normalizeOwnerValue(session.meta.plantId)
  const ownerEmployeeCode = normalizeOwnerValue(owner.employeeCode)
  const ownerPlantId = normalizeOwnerValue(owner.plantId)

  if (sessionEmployeeCode && ownerEmployeeCode) {
    if (sessionEmployeeCode !== ownerEmployeeCode) {
      return false
    }
  }

  if (sessionPlantId && ownerPlantId) {
    if (sessionPlantId !== ownerPlantId) {
      return false
    }
  }

  return true
}

export function doesOfflineIpqcBundleMatchPlant(
  bundle: OfflineIpqcBundle,
  plantId?: string
): boolean {
  const bundlePlantId = normalizeOwnerValue(bundle.context?.plantId)
  const currentPlantId = normalizeOwnerValue(plantId)

  if (!bundlePlantId || !currentPlantId) {
    return true
  }

  return bundlePlantId === currentPlantId
}
